// Fase 2, W13: formas das respostas do módulo Relatórios (modules/relatorios). Valores em centavos.

export interface Escopo {
  modo: 'empresa' | 'consolidado';
  empresa_id: string | null;
  empresas: { id: string; nome: string; arquivada: boolean }[];
}

export type Base = 'competencia' | 'caixa';
export type Intercompany = 'separados' | 'eliminados';
/** Os valores que a API aceita em `base` e `intercompany` (o que vier fora disso na URL é ignorado, `useParametro`). */
export const BASES: readonly Base[] = ['competencia', 'caixa'];
export const INTERCOMPANY: readonly Intercompany[] = ['separados', 'eliminados'];

export const LINHAS_DRE = [
  'receita_bruta',
  'deducoes',
  'receita_liquida',
  'custos',
  'lucro_bruto',
  'despesas_operacionais',
  'resultado_operacional',
  'resultado_financeiro',
  'nao_operacional',
  'sem_grupo',
  'sem_categoria',
  'resultado',
  'fora_dre',
] as const;
export type LinhaDre = (typeof LINHAS_DRE)[number];

export interface NoArvore {
  categoria_id: string | null;
  nome: string | null;
  codigo: string | null;
  proprio_centavos: number;
  total_centavos: number;
  filhos: NoArvore[];
  /** Fase 2, W24 (K9): categoria sintética (só agrupa; o valor é o das filhas). */
  sintetica?: boolean;
}

export interface Dre {
  base: Base;
  de: string;
  ate: string;
  escopo: Escopo;
  intercompany: Intercompany;
  parceiros_do_grupo: number;
  meses: string[] | null;
  por_moeda: {
    moeda: string;
    linhas: { chave: LinhaDre; tipo: 'grupo' | 'subtotal' | 'resultado' | 'balde' | 'fora'; valor_centavos: number; serie?: number[]; por_empresa?: Record<string, number>; eliminacoes_centavos?: number }[];
    arvore: { balde: string; total_centavos: number; nos: NoArvore[] }[];
    resultado_centavos: number;
  }[];
}

export interface ItemDetalhe {
  origem: 'lancamento' | 'documento' | 'apropriacao';
  id: string;
  registro_id: string | null;
  data: string;
  descricao: string;
  numero: string | null;
  componente: string | null;
  categoria_id: string | null;
  categoria_nome: string | null;
  parceiro_id: string | null;
  parceiro_nome: string | null;
  moeda: string;
  valor_centavos: number;
}

export interface Detalhe {
  itens: ItemDetalhe[];
  total: number;
  soma_por_moeda: { moeda: string; valor_centavos: number }[];
  truncado: boolean;
}

export const FAIXAS = ['a_vencer', '1_15', '16_30', '31_60', '61_90', 'mais_90'] as const;
export type Faixa = (typeof FAIXAS)[number];

export interface ValoresAging {
  documentos: number;
  principal_centavos: number;
  juros_centavos: number;
  multa_centavos: number;
  atualizado_centavos: number;
}

export interface Inadimplencia {
  em: string;
  hoje: string;
  escopo: Escopo;
  por_moeda: {
    moeda: string;
    faixas: (ValoresAging & { faixa: Faixa })[];
    total: ValoresAging;
    a_vencer_centavos: number;
    vencido_centavos: number;
    percentual_inadimplencia: string | null;
    creditos_centavos: number;
    clientes: (ValoresAging & {
      parceiro_id: string | null;
      nome: string | null;
      faixas: Record<Faixa, number>;
      vencido_centavos: number;
      creditos_centavos: number;
      liquido_centavos: number;
      dias_mais_antigo: number | null;
      atraso_medio_dias: number | null;
    })[];
  }[];
}

export interface TituloAging {
  id: string;
  numero: string;
  vencimento: string;
  dias: number;
  faixa: Faixa;
  principal_centavos: number;
  juros_centavos: number;
  multa_centavos: number;
  atualizado_centavos: number;
  moeda: string;
}

export interface PeriodoFluxo {
  inicio: string;
  fim: string;
  realizado_entradas_centavos: number;
  realizado_saidas_centavos: number;
  previsto_entradas_centavos: number;
  previsto_saidas_centavos: number;
  previsto_titulos_entradas_centavos: number;
  previsto_titulos_saidas_centavos: number;
  saldo_projetado_centavos: number;
}

export interface Fluxo {
  de: string;
  ate: string;
  hoje: string;
  agrupar: 'dia' | 'semana' | 'mes';
  conta_id: string | null;
  escopo: Escopo;
  previsto_com_titulos: boolean;
  por_moeda: {
    moeda: string;
    abertura_centavos: number;
    periodos: PeriodoFluxo[];
    totais: { realizado_entradas_centavos: number; realizado_saidas_centavos: number; previsto_entradas_centavos: number; previsto_saidas_centavos: number };
    saldo_final_projetado_centavos: number;
  }[];
}

export interface NoCategoriaFluxo {
  categoria_id: string | null;
  nome: string | null;
  codigo: string | null;
  entradas_centavos: number;
  saidas_centavos: number;
  resultado_centavos: number;
  filhos: NoCategoriaFluxo[];
}

export interface FluxoCategorias {
  escopo: Escopo;
  por_moeda: { moeda: string; entradas_centavos: number; saidas_centavos: number; resultado_centavos: number; arvore: NoCategoriaFluxo[] }[];
}

export interface LinhaExtrato {
  id: string;
  data_caixa: string;
  tipo: 'entrada' | 'saida' | 'transferencia';
  descricao: string;
  efeito_centavos: number;
  saldo_centavos: number;
  conciliado: boolean;
  estorno_de: string | null;
  estornado_por: string | null;
  categoria_nome: string | null;
  outra_conta_nome: string | null;
}

export interface Extrato {
  de: string;
  ate: string;
  escopo: Escopo;
  contas: {
    conta_id: string;
    conta_nome: string;
    moeda: string;
    ativa: boolean;
    saldo_anterior_centavos: number;
    entradas_centavos: number;
    saidas_centavos: number;
    saldo_final_centavos: number;
    linhas: LinhaExtrato[];
    total_linhas: number;
    truncado: boolean;
  }[];
  totais: { moeda: string; contas: number; saldo_anterior_centavos: number; entradas_centavos: number; saidas_centavos: number; saldo_final_centavos: number }[];
}

export const COLUNAS_CENTRO = ['receita_bruta', 'deducoes', 'custos', 'despesas_operacionais', 'resultado_financeiro', 'nao_operacional', 'outros'] as const;
export type ColunaCentro = (typeof COLUNAS_CENTRO)[number];

export interface LinhaCentro {
  centro_custo_id: string | null;
  nome: string | null;
  codigo: string | null;
  valores: Record<ColunaCentro, number>;
  resultado_centavos: number;
  fora_dre_centavos: number;
}

export interface Centros {
  escopo: Escopo;
  intercompany: Intercompany;
  por_moeda: { moeda: string; centros: LinhaCentro[]; total: LinhaCentro }[];
}

export interface Parceiros {
  escopo: Escopo;
  intercompany: Intercompany;
  por_moeda: {
    moeda: string;
    total_centavos: number;
    parceiros: { parceiro_id: string | null; nome: string | null; valor_centavos: number; participacao: string | null; acumulado: string | null; classe: 'A' | 'B' | 'C' | null }[];
    abc: { classe: 'A' | 'B' | 'C'; parceiros: number; valor_centavos: number }[];
  }[];
}

export interface Mrr {
  em: string;
  fonte: 'recorrencias' | 'contratos';
  escopo: Escopo;
  por_moeda: {
    moeda: string;
    mrr_centavos: number;
    arr_centavos: number;
    ativos: number;
    pausado_centavos: number;
    a_iniciar_centavos: number;
    por_periodicidade: { periodicidade: string; ativos: number; mrr_centavos: number }[];
    por_cliente: { parceiro_id: string | null; nome: string | null; ativos: number; mrr_centavos: number }[];
    por_empresa: { empresa_id: string | null; mrr_centavos: number }[];
  }[];
}

export interface Exportacao {
  id: string;
  relatorio: 'inadimplencia' | 'dre' | 'fluxo' | 'extrato' | 'centros' | 'parceiros' | 'mrr' | 'margem' | 'quadro' | 'folha' | 'painel_grupo';
  /** E1: o arquivo saiu mascarado; veio do agendamento; o relatório salvo de origem. */
  mascarado?: boolean;
  agendada?: boolean;
  salvo_nome?: string | null;
  parametros: Record<string, unknown>;
  estado: 'pendente' | 'pronto' | 'erro';
  arquivo_nome: string | null;
  linhas: number | null;
  erro: string | null;
  created_at: string;
  concluido_em: string | null;
}

/** Fase 2 (W21, K13): R8 · margem por serviço (`GET /relatorios/margem`). */
export interface ValoresMargem {
  cobrancas: number;
  contratos: number;
  receita_centavos: number;
  custo_centavos: number;
  margem_centavos: number;
  margem_pct: string | null;
}

export interface Margem {
  de: string;
  ate: string;
  disponivel: boolean;
  escopo: Escopo;
  intercompany: 'separados' | 'eliminados';
  por_moeda: {
    moeda: string;
    total: ValoresMargem;
    servicos: (ValoresMargem & { servico_id: string | null; nome: string })[];
    por_empresa: (ValoresMargem & { empresa_id: string | null })[];
  }[];
}

export interface TituloMargem {
  cobranca_id: string;
  fatura_id: string | null;
  contrato_id: string;
  contrato_numero: string;
  parceiro_id: string;
  parceiro_nome: string | null;
  competencia: string;
  vencimento: string;
  moeda: string;
  receita_centavos: number;
  custo_centavos: number;
  margem_centavos: number;
  margem_pct: string | null;
}

export interface TitulosMargem {
  itens: TituloMargem[];
  soma_por_moeda: { moeda: string; receita_centavos: number; custo_centavos: number; margem_centavos: number; margem_pct: string | null }[];
  total: number;
  truncado: boolean;
}

/** E1: quadro de pessoas (`GET /relatorios/quadro`), só contagens. */
export interface LinhaQuadro {
  departamento_id: string | null;
  departamento: string | null;
  funcao_id: string | null;
  funcao: string | null;
  no_periodo: number;
  ativos: number;
  afastados: number;
  admitidos: number;
  desligados: number;
}

export interface Quadro {
  de: string;
  ate: string;
  escopo: Escopo;
  linhas: LinhaQuadro[];
  totais: Omit<LinhaQuadro, 'departamento_id' | 'departamento' | 'funcao_id' | 'funcao'>;
}

/** E1: folha por competência (`GET /relatorios/folha`), só os totais de cada competência. */
export interface CompetenciaFolha {
  competencia_id: string;
  ano_mes: string;
  empresa_id: string | null;
  estado: 'aberta' | 'calculada' | 'conferida' | 'fechando' | 'fechada';
  funcionarios: number;
  proventos_centavos: number;
  descontos_centavos: number;
  encargos_centavos: number;
  liquido_centavos: number;
  moeda: string;
}

export interface Folha {
  de_mes: string;
  ate_mes: string;
  escopo: Escopo;
  competencias: CompetenciaFolha[];
  totais: { moeda: string; competencias: number; proventos_centavos: number; descontos_centavos: number; encargos_centavos: number; liquido_centavos: number; custo_centavos: number }[];
}

/** Lote 19: painel do grupo de empresas (`GET /relatorios/painel-grupo`). */
export const INDICADORES_PAINEL = [
  'receita_realizada',
  'despesa_realizada',
  'resultado',
  'receber_aberto',
  'receber_vencido',
  'pagar_aberto',
  'pagar_vencido',
  'saldo_contas',
  'mrr',
  'contratos_ativos',
  'funcionarios_ativos',
] as const;

export interface ValorIndicadorPainel {
  moeda: string | null;
  por_empresa: Record<string, number>;
  eliminacoes: number;
  total: number;
}

export interface IndicadorPainel {
  chave: (typeof INDICADORES_PAINEL)[number];
  modulo: 'financeiro' | 'contas' | 'assinaturas' | 'pessoas';
  formato: 'dinheiro' | 'quantidade';
  ocultado: boolean;
  valores: ValorIndicadorPainel[];
}

export interface PainelGrupo {
  de: string;
  ate: string;
  hoje: string;
  realizado_ate: string;
  saldo_em: string;
  intercompany: 'separados' | 'eliminados';
  empresas: { id: string; nome: string }[];
  indicadores: IndicadorPainel[];
}
